import React, { useMemo } from 'react';
import { Box, Chip, Typography, Tooltip, Select, MenuItem, FormControl, InputLabel, Checkbox, ListItemText } from '@mui/material';
import { IAsset, IBadge, ICategory } from '../types/damTypes';

interface BadgeFilterProps {
  assets: IAsset[];
  badges?: IBadge[]; // All configured badges (independent of loaded assets)
  selectedBadgeIds: string[];
  onBadgeToggle: (badgeId: string) => void;
  onClearBadges?: () => void;
  loading?: boolean;
  filterMode?: 'AND' | 'OR';
  onFilterModeToggle?: () => void;
  activeCategoryIds?: string[];
  allCategories?: ICategory[];
  compact?: boolean; // Render as dropdown instead of chip row
}

const BadgeFilter: React.FC<BadgeFilterProps> = ({
  assets,
  badges,
  selectedBadgeIds,
  onBadgeToggle,
  onClearBadges,
  loading = false,
  filterMode = 'AND',
  onFilterModeToggle = () => {},
  activeCategoryIds = [],
  allCategories = [],
  compact = false,
}) => {
  // Build the list of visible badges.
  // If a pre-fetched `badges` list is provided, use it (so we're not limited to
  // the currently loaded assets). Otherwise fall back to scanning loaded assets.
  const activeBadges = useMemo(() => {
    const badgeMap = new Map<string, IBadge>();

    // Expand selected categories with their ancestors so parent-configured badges appear in subcategories
    const expandedSelected = new Set<string>(activeCategoryIds);
    if (activeCategoryIds.length > 0 && allCategories && allCategories.length > 0) {
      const idToParent: Record<string, string | null> = {};
      allCategories.forEach(cat => {
        let parentId: string | null = null;
        if (typeof cat.parentCategory === 'string') parentId = cat.parentCategory;
        else if (cat.parentCategory && typeof cat.parentCategory === 'object') parentId = (cat.parentCategory as any)._id;
        idToParent[cat._id] = parentId;
      });
      activeCategoryIds.forEach(id => {
        let current = idToParent[id];
        while (current) {
          expandedSelected.add(current);
          current = idToParent[current];
        }
      });
    }

    const candidateBadges: IBadge[] = badges
      ? badges
      : // Fallback: extract populated badge objects from loaded assets
        (() => {
          const seen = new Map<string, IBadge>();
          assets.forEach(asset => {
            if (asset.activeBadges) {
              asset.activeBadges.forEach(b => {
                if (typeof b === 'object' && b._id) seen.set(b._id, b);
              });
            }
          });
          return Array.from(seen.values());
        })();

    candidateBadges.forEach(badge => {
      if (typeof badge.assetCount === 'number' && badge.assetCount === 0) return;
      const visibleInAll = badge.visibleInAll !== false;
      const visibleInCategories = (badge.visibleInCategories || []) as unknown as string[];
      let shouldInclude = activeCategoryIds.length === 0 ? visibleInAll : false;
      if (!shouldInclude && activeCategoryIds.length > 0) {
        const selectedArray = Array.from(expandedSelected);
        shouldInclude = visibleInCategories.some(id => selectedArray.includes(id));
      }
      if (shouldInclude) {
        badgeMap.set(badge._id, badge);
      }
    });

    // Sort badges by configured displayOrder first, then by createdAt/name as fallback
    return Array.from(badgeMap.values()).sort((a, b) => {
      const ao = (a.displayOrder ?? 0);
      const bo = (b.displayOrder ?? 0);
      if (ao !== bo) return ao - bo;
      const ad = (a as any).createdAt ? new Date((a as any).createdAt).getTime() : 0;
      const bd = (b as any).createdAt ? new Date((b as any).createdAt).getTime() : 0;
      if (ad !== bd) return ad - bd;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [assets, badges, activeCategoryIds, allCategories]);

  // Count assets for each badge
  const badgeCounts = useMemo(() => {
    const counts: { [badgeId: string]: number } = {};
    
    assets.forEach(asset => {
      if (asset.activeBadges && asset.activeBadges.length > 0) {
        asset.activeBadges.forEach(badge => {
          const badgeId = typeof badge === 'object' ? badge._id : badge;
          if (badgeId) {
            counts[badgeId] = (counts[badgeId] || 0) + 1;
          }
        });
      }
    });
    
    return counts;
  }, [assets]);

  if (loading || activeBadges.length === 0) {
    return null;
  }

  // Compact mode: dropdown
  if (compact) {
    return (
      <FormControl size="small" sx={{ minWidth: 100, order: 4 }}>
        <InputLabel id="badge-filter-label" sx={{ fontSize: '0.75rem' }}>Märken</InputLabel>
        <Select
          labelId="badge-filter-label"
          label="Märken"
          multiple
          value={selectedBadgeIds}
          onChange={(e) => {
            const newVal = e.target.value as string[];
            // Toggle logic: find diff
            const added = newVal.filter(id => !selectedBadgeIds.includes(id));
            const removed = selectedBadgeIds.filter(id => !newVal.includes(id));
            [...added, ...removed].forEach(id => onBadgeToggle(id));
          }}
          renderValue={(selected) =>
            selected.length === 0
              ? ''
              : `${selected.length} vald${selected.length > 1 ? 'a' : ''}`
          }
          sx={{ height: 32, fontSize: '0.75rem', bgcolor: '#f8f8f8' }}
        >
          {activeBadges.map(badge => (
            <MenuItem key={badge._id} value={badge._id} dense>
              <Checkbox size="small" checked={selectedBadgeIds.includes(badge._id)} sx={{ p: 0.25 }} />
              <ListItemText
                primary={`${badge.displayEmoji || ''} ${badge.name}`.trim()}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                sx={{ ml: 0.5 }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'row', 
      alignItems: 'center',
      gap: 1,
      background: '#f8f8f8',
      padding: '4px 8px',
      borderRadius: '4px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      minHeight: '40px',
      flexWrap: 'wrap',
      overflowX: 'auto',
      maxWidth: '100%'
    }}>
      {activeBadges.length > 0 && (
        <>
          {/* Label removed per UX request */}
          
          {activeBadges.map(badge => {
            const isSelected = selectedBadgeIds.includes(badge._id);
            const count = badgeCounts[badge._id] || 0;
            const label = `${(badge.iconUrl ? '' : (badge.displayEmoji || ''))} ${(badge.showName !== false) ? badge.name : ''}`.trim() || badge.name;
            
            return (
              <Tooltip 
                key={badge._id} 
                title={`${count} asset${count !== 1 ? 's' : ''} med detta märke`}
                arrow
              >
                <Chip
                  label={label}
                  onClick={() => onBadgeToggle(badge._id)}
                  variant={isSelected ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    height: '24px',
                    fontSize: '11px',
                    backgroundColor: isSelected ? badge.color : 'transparent',
                    color: isSelected ? badge.textColor : badge.color,
                    borderColor: badge.color,
                    fontWeight: isSelected ? 'bold' : 'normal',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: isSelected 
                        ? badge.color 
                        : `${badge.color}20`, // 20% opacity
                      transform: 'scale(1.05)'
                    }
                  }}
                />
              </Tooltip>
            );
          })}
          
          {selectedBadgeIds.length > 1 && (
            <Chip
              label={filterMode}
              onClick={onFilterModeToggle}
              variant="filled"
              size="small"
              sx={{
                height: '24px',
                fontSize: '10px',
                ml: 1,
                backgroundColor: filterMode === 'AND' ? '#2196f3' : '#ff9800',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: filterMode === 'AND' ? '#1976d2' : '#f57c00'
                }
              }}
            />
          )}
          
          {selectedBadgeIds.length > 0 && (
            <Chip
              label="Rensa"
              onClick={() => onClearBadges ? onClearBadges() : selectedBadgeIds.forEach(id => onBadgeToggle(id))}
              variant="outlined"
              size="small"
              sx={{
                height: '20px',
                fontSize: '10px',
                ml: 1,
                color: '#666',
                borderColor: '#666',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default BadgeFilter; 